import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// iter148 — Tab-close logout detection.
//
// Goal: closing the browser tab MUST force a logout on next visit,
// but a mere page refresh MUST NOT. iOS PWA standalone mode MUST be
// exempt (deleting the app from the home screen wipes localStorage
// anyway, which yields the same outcome the user asked for).
//
// Approach (well-established pattern):
//   • sessionStorage `orb_tab_session` is set on every successful
//     auth check. sessionStorage persists across refresh but is
//     wiped on tab close — exactly the signal we need.
//   • On AuthContext mount, if localStorage.orb_token is present AND
//     sessionStorage.orb_tab_session is MISSING, the tab was closed
//     since the last session → check BroadcastChannel for other
//     live tabs first (so opening a 2nd tab while the 1st is still
//     open does NOT force logout). If still alone → force-logout.
//   • iOS PWA (`navigator.standalone === true` OR display-mode
//     standalone) bypasses the whole flow.
//
// Per-tab ID note: BroadcastChannel routes messages to ALL channels
// in the same origin — including SECONDARY channels in the SAME tab.
// We carry a randomly generated tab id in every ping/pong so the
// requesting tab can ignore its own echoes. Without this, the
// listener registered later in this same module would respond to
// our own ping and the tab-close detector would always think a peer
// tab was alive.
const TAB_SESSION_KEY = 'orb_tab_session';
const TAB_BC_NAME = 'orb-tab-presence';
let TAB_ID = null;
const getTabId = () => {
  if (TAB_ID) return TAB_ID;
  try {
    const arr = new Uint32Array(2);
    (window.crypto || window.msCrypto).getRandomValues(arr);
    TAB_ID = `${arr[0].toString(36)}-${arr[1].toString(36)}`;
  } catch (_) {
    TAB_ID = String(Math.random()).slice(2);
  }
  return TAB_ID;
};

const isStandalonePWA = () => {
  if (typeof window === 'undefined') return false;
  const iosStandalone = typeof navigator !== 'undefined' && navigator.standalone === true;
  let displayStandalone = false;
  try {
    displayStandalone = window.matchMedia('(display-mode: standalone)').matches;
  } catch (_) { /* ignore */ }
  return iosStandalone || displayStandalone;
};

// Probe BroadcastChannel for another live tab. Returns true if any
// OTHER tab (different tab id) responds within ~250ms. Falls back to
// `false` when BroadcastChannel is unavailable (older Safari) — safer
// to log out than to leak a session in those cases.
const anyOtherTabAlive = () => new Promise((resolve) => {
  if (typeof BroadcastChannel === 'undefined') {
    resolve(false);
    return;
  }
  let ch;
  try { ch = new BroadcastChannel(TAB_BC_NAME); } catch (_) { resolve(false); return; }
  let settled = false;
  const myId = getTabId();
  const finish = (val) => {
    if (settled) return;
    settled = true;
    try { ch.close(); } catch (_) { /* ignore */ }
    resolve(val);
  };
  ch.onmessage = (ev) => {
    const d = ev?.data || {};
    if (d.type === 'pong' && d.from && d.from !== myId) finish(true);
  };
  try { ch.postMessage({ type: 'ping', from: myId }); } catch (_) { finish(false); return; }
  setTimeout(() => finish(false), 250);
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('orb_token'));
  const [loading, setLoading] = useState(true);

  // iter148 — Respond to BroadcastChannel pings so peer tabs know
  // we're alive. Without this the "tab close = logout" detection
  // would falsely fire on the SECOND tab of a multi-tab user. We
  // ignore pings carrying our own tab id so the SAME-page listener
  // doesn't echo back to its own probe.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    let ch;
    try { ch = new BroadcastChannel(TAB_BC_NAME); } catch (_) { return undefined; }
    const myId = getTabId();
    ch.onmessage = (ev) => {
      const d = ev?.data || {};
      if (d.type === 'ping' && d.from && d.from !== myId) {
        try { ch.postMessage({ type: 'pong', from: myId }); } catch (_) { /* ignore */ }
      }
    };
    return () => { try { ch.close(); } catch (_) { /* ignore */ } };
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        // iter148 — Decide whether this is a refresh (keep logged in)
        // or a fresh tab open after close (force logout). PWA users
        // bypass this entirely so deleting the home-screen app — which
        // clears localStorage — is the only logout signal.
        const tabAlive = sessionStorage.getItem(TAB_SESSION_KEY);
        if (!tabAlive && !isStandalonePWA()) {
          const peerAlive = await anyOtherTabAlive();
          if (!peerAlive) {
            // Tab close detected — wipe localStorage and force re-login.
            localStorage.removeItem('orb_token');
            setToken(null);
            setUser(null);
            setLoading(false);
            return;
          }
        }
        // Mark this tab as alive so a refresh keeps us logged in.
        try { sessionStorage.setItem(TAB_SESSION_KEY, '1'); } catch (_) { /* ignore */ }
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error('Auth init error:', error);
          localStorage.removeItem('orb_token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  // iter125 — Broadcast the user's persisted theme + desktop_view
  // preferences whenever the user object refreshes (post-login,
  // post-profile-edit). ThemeContext listens for this event and
  // hydrates its in-memory state so settings survive across devices
  // and new sessions.
  useEffect(() => {
    if (!user) return;
    window.dispatchEvent(new CustomEvent('orb-user-prefs-loaded', {
      detail: { theme: user.theme, desktop_view: user.desktop_view },
    }));
  }, [user]);

  const login = async (email, password, totpCode = null) => {
    const payload = { email, password };
    if (totpCode) {
      payload.totp_code = totpCode;
    }
    const response = await axios.post(`${API}/auth/login`, payload);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('orb_token', access_token);
    try {
      sessionStorage.setItem(TAB_SESSION_KEY, '1');
      // iter150 — Clear any stale reauth markers from a prior session
      // in this same tab. Otherwise User A reauthenticating, logging
      // out, and User B logging in within the validity window would
      // give B the still-valid reauth timestamp and silently bypass
      // their own gating policy.
      sessionStorage.removeItem('orb_reauth_at');
      sessionStorage.removeItem('orb_reauth_ttl');
      sessionStorage.removeItem('orb_reauth_policy_cache');
    } catch (_) { /* ignore */ }
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    localStorage.setItem('orb_token', access_token);
    try {
      sessionStorage.setItem(TAB_SESSION_KEY, '1');
      sessionStorage.removeItem('orb_reauth_at');
      sessionStorage.removeItem('orb_reauth_ttl');
      sessionStorage.removeItem('orb_reauth_policy_cache');
    } catch (_) { /* ignore */ }
    setToken(access_token);
    setUser(newUser);
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem('orb_token');
    try {
      sessionStorage.removeItem(TAB_SESSION_KEY);
      // iter150 — Wipe the reauth + policy cache on logout so the next
      // signed-in user starts with a clean slate.
      sessionStorage.removeItem('orb_reauth_at');
      sessionStorage.removeItem('orb_reauth_ttl');
      sessionStorage.removeItem('orb_reauth_policy_cache');
    } catch (_) { /* ignore */ }
    setToken(null);
    setUser(null);
  };

  // Sign in using an Uplift bearer token minted elsewhere (e.g. the
  // ORB iframe ticket exchange). Persists to localStorage, updates
  // React state synchronously, and hydrates the user profile so
  // ProtectedRoute flips to authenticated without a page reload.
  const loginWithToken = async (accessToken, prefetchedUser = null) => {
    if (!accessToken) return null;
    localStorage.setItem('orb_token', accessToken);
    try { sessionStorage.setItem(TAB_SESSION_KEY, '1'); } catch (_) { /* ignore */ }
    setToken(accessToken);
    if (prefetchedUser) {
      setUser(prefetchedUser);
      setLoading(false);
      return prefetchedUser;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      setUser(response.data);
      setLoading(false);
      return response.data;
    } catch (error) {
      console.error('loginWithToken: failed to hydrate user', error);
      localStorage.removeItem('orb_token');
      setToken(null);
      setUser(null);
      setLoading(false);
      throw error;
    }
  };

  const updateUser = async (updateData) => {
    const response = await axios.put(`${API}/auth/me`, updateData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser(response.data);
    return response.data;
  };

  // Refresh user data from server
  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        return response.data;
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    }
    return null;
  };

  const api = axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    timeout: 120000, // 2 minute timeout for large file uploads
    maxContentLength: Infinity,
    maxBodyLength: Infinity
  });

  // Add response interceptor for better error logging and account lock handling
  api.interceptors.response.use(
    response => response,
    error => {
      if (error.response) {
        console.error('API Error Response:', error.response.status, error.response.data);
        
        // Check if account is locked - force logout
        if (error.response.status === 403 && error.response.data?.detail === 'ACCOUNT_LOCKED') {
          console.warn('Account locked - forcing logout');
          localStorage.removeItem('orb_token');
          // iter151 — Wipe the reauth + policy cache on forced logout
          // too. Without this, the next user to sign in on the same
          // tab inherits the previous user's reauth-valid window and
          // skips the page-gate modals until it lapses. (iter149
          // patched the explicit logout() path; this 401/403
          // interceptor path was missed.)
          try {
            sessionStorage.removeItem(TAB_SESSION_KEY);
            sessionStorage.removeItem('orb_reauth_at');
            sessionStorage.removeItem('orb_reauth_ttl');
            sessionStorage.removeItem('orb_reauth_policy_cache');
          } catch (_) { /* ignore */ }
          setToken(null);
          setUser(null);
          // Redirect to login page with message
          window.location.href = '/?locked=true';
          return Promise.reject(new Error('Account is locked. You have been logged out.'));
        }
        
        // Also logout on 401 errors (token expired/invalid)
        if (error.response.status === 401) {
          localStorage.removeItem('orb_token');
          try {
            sessionStorage.removeItem(TAB_SESSION_KEY);
            sessionStorage.removeItem('orb_reauth_at');
            sessionStorage.removeItem('orb_reauth_ttl');
            sessionStorage.removeItem('orb_reauth_policy_cache');
          } catch (_) { /* ignore */ }
          setToken(null);
          setUser(null);
        }
      } else if (error.request) {
        console.error('API No Response:', error.message);
      } else {
        console.error('API Request Error:', error.message);
      }
      return Promise.reject(error);
    }
  );

  // Check if a feature is enabled for the current user
  const isFeatureEnabled = useCallback((featureId) => {
    // If no user or no feature_toggles, feature is enabled by default
    if (!user || !user.feature_toggles) return true;
    
    // Check if the feature is explicitly disabled
    const toggle = user.feature_toggles[featureId];
    
    // If toggle is undefined, feature is enabled by default
    // If toggle is false, feature is disabled
    // If toggle is true, feature is enabled
    return toggle !== false;
  }, [user]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      loginWithToken,
      updateUser,
      refreshUser,
      api,
      isAuthenticated: !!user,
      isFeatureEnabled
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
